import {useEffect, useMemo, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {Link, useNavigate, useParams, useSearchParams} from "react-router-dom";
import {Layers, Network, Search, Settings2, Users} from "lucide-react";
import {useCapabilities} from "../../shared/auth/CapabilitiesContext";
import {ADMIN_ROLES} from "../../shared/auth/roles";
import {ReferralGraphCanvas} from "../../shared/components/BusinessBlocks";
import {AdminTable} from "../../shared/components/AdminTable";
import {DataState} from "../../shared/components/DataState";
import {PageToolbar, ToolbarButton, ToolbarInput, ToolbarSelect} from "../../shared/components/PageToolbar";
import {ServerPagination} from "../../shared/components/ServerPagination";
import {StatusBadge} from "../../shared/components/StatusBadge";
import {asList, formatMoney, formatNumber, getCount} from "../../shared/utils/format";
import {useActiveAdminCountryScope} from "../organizations/useActiveAdminCountryScope";
import {
    getReferralCredentials,
    getReferralNetwork,
    listReferralProperties,
    listReferrals,
    referralKeys,
} from "./api";
import {countByReferralLevel} from "../referralPolicies/contracts";
import {flattenReferralNetwork, pageFromReferralLink} from "./contracts";
import "../referralPolicies/referralPolicies.css";
import {
    CredentialEditorDialog,
    GlobalReferralSearchDialog,
    ReferralDetailDialog,
} from "./ReferralDialogs";
import {ReferralMoveWorkspace} from "./ReferralMoveWorkspace";
import {PlaceReferralWorkspace} from "./PlaceReferralWorkspace";
import {DeployerReferralWorkspace} from "./DeployerReferralWorkspace";
import "./referrals.css";
import {useTranslation, i18n} from "../../shared/i18n";


const PLATFORM_ROLES = new Set([ADMIN_ROLES.SUPERADMIN, ADMIN_ROLES.ADMINISTRATOR]);
const PLACE_WORKSPACE_ROLES = new Set([ADMIN_ROLES.OWNER]);

const ReferralRegistryPage = ({capabilities, countryScope}) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [mode, setMode] = useState("places");
    const [draft, setDraft] = useState({search: "", city_id: "", category_id: ""});
    const [filters, setFilters] = useState({});
    const [page, setPage] = useState(1);
    const [credentialsOpen, setCredentialsOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [selectedPlace, setSelectedPlace] = useState(null);
    const canView = capabilities.hasAction("referral.view");
    const canManage = capabilities.hasAction("referral.manage");
    const canManageGlobalLevels = capabilities.hasAction("system.manage")
        && capabilities.user?.role === ADMIN_ROLES.SUPERADMIN;

    useEffect(() => {
        setDraft({search: "", city_id: "", category_id: ""});
        setFilters({});
        setPage(1);
        setSelectedPlace(null);
    }, [countryScope]);

    const referralsQuery = useQuery({
        queryKey: referralKeys.list(countryScope, mode, filters, page),
        queryFn: () => listReferrals({destination: mode, page, countryId: countryScope, filters}),
        enabled: canView,
    });
    const propertiesQuery = useQuery({
        queryKey: referralKeys.properties(countryScope),
        queryFn: () => listReferralProperties({countryId: countryScope}),
        enabled: canView && mode === "places",
    });
    const credentialsQuery = useQuery({
        queryKey: referralKeys.credentials(),
        queryFn: getReferralCredentials,
        enabled: canManageGlobalLevels,
        retry: false,
    });

    const rows = asList(referralsQuery.data);
    const cities = propertiesQuery.data?.cities || propertiesQuery.data?.filterCities || [];
    const categories = propertiesQuery.data?.sub_categories
        || propertiesQuery.data?.categories
        || propertiesQuery.data?.filterCategories
        || [];
    const previousPage = pageFromReferralLink(referralsQuery.data?.previous);
    const nextPage = pageFromReferralLink(referralsQuery.data?.next);
    const summary = useMemo(() => ({
        participants: getCount(referralsQuery.data),
        network: rows.reduce((total, row) => total + Number(row.net_count || 0), 0),
        balance: rows.reduce((total, row) => total + Number(row.sum || 0), 0),
    }), [referralsQuery.data, rows]);

    const changeMode = (event) => {
        setMode(event.target.value);
        setDraft({search: "", city_id: "", category_id: ""});
        setFilters({});
        setPage(1);
    };
    const applyFilters = () => {
        setPage(1);
        setFilters({
            search: draft.search.trim(),
            ...(mode === "places" ? {city_id: draft.city_id, category_id: draft.category_id} : {city_id: draft.city_id}),
        });
    };
    const clearFilters = () => {
        setDraft({search: "", city_id: "", category_id: ""});
        setFilters({});
        setPage(1);
    };

    const columns = [
        {
            key: "name",
            label: mode === "places" ? t("sections.referrals.p54") : t("sections.referrals.p13"),
            render: (row) => (
                <div>
                    <span className="admin-strong">{row.name || row.phone_number || `#${row.id}`}</span>
                    <div className="admin-muted">ID {row.id}</div>
                </div>
            ),
        },
        {key: "city", label: t("sections.referrals.p64"), render: (row) => row.city || "—"},
        ...(mode === "places" ? [{key: "category", label: t("sections.referrals.p65"), render: (row) => row.category || "—"}] : []),
        ...(mode === "users" ? [{key: "phone", label: t("sections.referrals.p66"), render: (row) => row.phone_number || "—"}] : []),
        {key: "network_count", label: t("sections.referrals.p67"), render: (row) => formatNumber(row.net_count)},
        {key: "sum", label: t("sections.referrals.p68"), render: (row) => formatMoney(row.sum)},
        ...(mode === "users" ? [{key: "bonus_balance", label: t("sections.referrals.p69"), render: (row) => formatMoney(row.bonus_balance)}] : []),
        {
            key: "actions",
            label: t("sections.referrals.p70"),
            render: (row) => (
                <span className="admin-inline-actions referral-row-actions">
                    <Link className="admin-button admin-button-secondary" to={`/referral/${row.id}`}>{i18n.t("sections.referrals.p71")}</Link>
                    {mode === "places" && canManage ? (
                        <button type="button" className="admin-button admin-button-secondary" aria-label={i18n.t("sections.referrals.p72", {v0: row.name || row.id})} onClick={() => setSelectedPlace(row)}>
                            <Settings2 aria-hidden="true" size={14} />
                        </button>
                    ) : null}
                </span>
            ),
        },
    ];

    if (!canView) {
        return <DataState title={t("sections.referrals.p73")} copy={t("sections.referrals.p74")} />;
    }

    return (
        <div className="admin-page">
            <header className="admin-page-header">
                <div>
                    <p className="admin-eyebrow">{t("sections.referrals.p75")}</p>
                    <h1 className="admin-page-title">{t("sections.referrals.p76")}</h1>
                    <p className="admin-page-copy">{t("sections.referrals.p77")}</p>
                </div>
                <div className="admin-inline-actions">
                    <button type="button" className="admin-button admin-button-secondary" onClick={() => setSearchOpen(true)}>
                        <Search aria-hidden="true" size={15} /> {t("sections.referrals.p37")}
                    </button>
                    {canManage ? (
                        <Link className="admin-button admin-button-secondary" to="/referral/policies">
                            <Layers aria-hidden="true" size={15} /> {t("sections.referrals.p78")}
                        </Link>
                    ) : null}
                    {canManageGlobalLevels ? (
                        <button type="button" className="admin-button admin-button-primary" onClick={() => setCredentialsOpen(true)}>
                            <Settings2 aria-hidden="true" size={15} /> {t("sections.referrals.p79")}
                        </button>
                    ) : null}
                </div>
            </header>

            <section className="admin-grid cols-3">
                <article className="admin-card">
                    <p className="admin-card-title">{t("sections.referrals.p80")}</p>
                    <p className="admin-card-value">{formatNumber(summary.participants)}</p>
                </article>
                <article className="admin-card">
                    <p className="admin-card-title"><Users size={14} /> {t("sections.referrals.p81")}</p>
                    <p className="admin-card-value">{formatNumber(summary.network)}</p>
                </article>
                <article className="admin-card">
                    <p className="admin-card-title">{t("sections.referrals.p82")}</p>
                    <p className="admin-card-value">{formatMoney(summary.balance)}</p>
                    {canManageGlobalLevels ? <p className="admin-card-note">{credentialsQuery.data?.text || t("sections.referrals.p83")}</p> : null}
                </article>
            </section>

            <PageToolbar>
                <ToolbarSelect label={t("sections.referrals.p84")} value={mode} onChange={changeMode}>
                    <option value="places">{t("sections.referrals.p85")}</option>
                    <option value="users">{t("sections.referrals.p86")}</option>
                </ToolbarSelect>
                <ToolbarInput label={t("sections.referrals.p87")} placeholder={mode === "places" ? t("sections.referrals.p56") : t("sections.referrals.p88")} value={draft.search} onChange={(event) => setDraft((current) => ({...current, search: event.target.value}))} />
                <ToolbarSelect label={t("sections.referrals.p64")} value={draft.city_id} onChange={(event) => setDraft((current) => ({...current, city_id: event.target.value}))}>
                    <option value="">{t("sections.referrals.p89")}</option>
                    {cities.map((city) => <option key={city.id} value={city.id}>{city.name}</option>)}
                </ToolbarSelect>
                {mode === "places" ? (
                    <ToolbarSelect label={t("sections.referrals.p65")} value={draft.category_id} onChange={(event) => setDraft((current) => ({...current, category_id: event.target.value}))}>
                        <option value="">{t("sections.referrals.p90")}</option>
                        {categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
                    </ToolbarSelect>
                ) : null}
                <ToolbarButton onClick={applyFilters}>{t("sections.referrals.p91")}</ToolbarButton>
                <ToolbarButton variant="secondary" onClick={clearFilters}>{t("sections.referrals.p92")}</ToolbarButton>
            </PageToolbar>

            {referralsQuery.isError ? (
                <DataState title={t("sections.referrals.p93")} copy={t("sections.referrals.p94")} />
            ) : (
                <>
                    <AdminTable columns={columns} rows={rows} loading={referralsQuery.isLoading} emptyText={t("sections.referrals.p95")} />
                    <ServerPagination count={referralsQuery.data?.count} page={page} previous={previousPage} next={nextPage} pending={referralsQuery.isFetching} onPageChange={setPage} />
                </>
            )}

            <CredentialEditorDialog open={credentialsOpen} credentials={credentialsQuery.data} onClose={() => setCredentialsOpen(false)} />
            <GlobalReferralSearchDialog open={searchOpen} countryId={countryScope} onClose={() => setSearchOpen(false)} onFound={(link) => navigate(link)} />
            <ReferralDetailDialog row={selectedPlace} countryId={countryScope} canManage={canManage} onClose={() => setSelectedPlace(null)} />
        </div>
    );
};

export const ReferralsPage = () => {
    const {t} = useTranslation();
    const capabilities = useCapabilities();
    const {countryScope} = useActiveAdminCountryScope(capabilities);
    const role = capabilities.user?.role;

    if (role === ADMIN_ROLES.DEPLOYER || role === ADMIN_ROLES.IMPLEMENTATION_HEAD) {
        return <DeployerReferralWorkspace />;
    }
    if (PLACE_WORKSPACE_ROLES.has(role)) {
        return <PlaceReferralWorkspace countryId={countryScope} canManage={capabilities.hasAction("referral.manage")} />;
    }
    if (!PLATFORM_ROLES.has(role)) {
        return <DataState title={t("sections.referrals.p73")} copy={t("sections.referrals.p96")} />;
    }
    return <ReferralRegistryPage capabilities={capabilities} countryScope={countryScope} />;
};

// «На уровне N — X человек». Ручка /network отдаёт полное поддерево без
// ограничения глубины, глубина уже считается flattenReferralNetwork — бэкенд не нужен.
const ReferralLevelCounts = ({node}) => {
    const {t} = useTranslation();
    const counts = useMemo(() => countByReferralLevel(flattenReferralNetwork(node)), [node]);
    const total = counts.reduce((sum, item) => sum + item.count, 0);

    return (
        <article className="admin-card">
            <p className="admin-card-title"><Users size={14} /> {t("sections.referrals.p97")}</p>
            <p className="admin-card-note">{t("sections.referrals.p98")} {formatNumber(total)}.</p>
            <div className="referral-policy-levels-summary">
                {counts.map((item) => (
                    <div key={item.level}>
                        <strong>{formatNumber(item.count)}</strong>
                        <span>{i18n.t("sections.referrals.p32")} {item.level}</span>
                    </div>
                ))}
            </div>
        </article>
    );
};

export const ReferralNetworkPage = () => {
    const {t} = useTranslation();
    const {ref_id: refId} = useParams();
    const [searchParams] = useSearchParams();
    const capabilities = useCapabilities();
    const {countryScope} = useActiveAdminCountryScope(capabilities);
    const canView = capabilities.hasAction("referral.view");
    const canManage = capabilities.hasAction("referral.manage");
    const networkQuery = useQuery({
        queryKey: referralKeys.network(countryScope, refId),
        queryFn: () => getReferralNetwork({refId, countryId: countryScope}),
        enabled: canView && Boolean(refId),
    });

    if (!canView) {
        return <DataState title={t("sections.referrals.p99")} copy={t("sections.referrals.p74")} />;
    }

    return (
        <div className="admin-page">
            <header className="admin-page-header">
                <div>
                    <p className="admin-eyebrow">{t("sections.referrals.p100")}</p>
                    <h1 className="admin-page-title">{t("sections.referrals.p101")}{refId}</h1>
                    <p className="admin-page-copy">{t("sections.referrals.p102")}</p>
                </div>
                <div className="admin-pill"><Network size={14} /> {t("sections.referrals.p103")}</div>
            </header>
            {networkQuery.isLoading ? (
                <DataState title={t("sections.referrals.p104")} copy={t("sections.referrals.p105")} />
            ) : networkQuery.isError ? (
                <DataState title={t("sections.referrals.p106")} copy={t("sections.referrals.p107")} />
            ) : (
                <>
                    <ReferralLevelCounts node={networkQuery.data} />
                    <ReferralGraphCanvas node={networkQuery.data} />
                    {canManage ? (
                        <ReferralMoveWorkspace
                            network={networkQuery.data}
                            refId={refId}
                            countryId={countryScope}
                            initialUserId={searchParams.get("search") || ""}
                        />
                    ) : (
                        <div className="referral-readonly-note">
                            <StatusBadge tone="neutral">{t("sections.referrals.p108")}</StatusBadge>
                            <span>{t("sections.referrals.p109")}</span>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
